import polyglotI18nProvider from 'ra-i18n-polyglot';
import type { TranslationMessages } from 'react-admin';
import { en } from './en/index';
import { zhTW } from './zh-TW/index';

export type AvailableLanguage = 'en' | 'zh-TW';

const translations = {
  en,
  'zh-TW': zhTW,
};

// Get default language from localStorage, browser, or fallback to English
export const getDefaultLanguage = (): AvailableLanguage => {
  try {
    // Check if localStorage is available
    if (typeof localStorage !== 'undefined' && localStorage) {
      // First check React Admin's locale storage
      const raLocale = localStorage.getItem('RaStore.locale');
      if (raLocale && (raLocale === 'en' || raLocale === 'zh-TW')) {
        return raLocale as AvailableLanguage;
      }
      
      // Then check our custom language storage
      const savedLanguage = localStorage.getItem('language');
      if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'zh-TW')) {
        return savedLanguage as AvailableLanguage;
      }
    }
  } catch (error) {
    console.warn('Failed to read language from localStorage:', error);
  }

  // Then check browser language
  try {
    const browserLanguage = navigator.language || navigator.languages[0];
    
    // Map browser languages to our supported languages
    if (browserLanguage.startsWith('zh-TW') || browserLanguage.startsWith('zh-Hant')) {
      return 'zh-TW';
    } else if (browserLanguage.startsWith('zh')) {
      // For other Chinese variants, use Traditional Chinese
      return 'zh-TW';
    }
  } catch (error) {
    console.warn('Failed to detect browser language:', error);
  }
  
  // Default to English
  return 'en';
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const deepMerge = <T>(base: T, override: T): T => {
  if (!isPlainObject(base) || !isPlainObject(override)) {
    return (override === undefined ? base : override) as T;
  }
  const merged: Record<string, unknown> = { ...base };
  for (const key of Object.keys(override)) {
    const overrideValue = (override as Record<string, unknown>)[key];
    const baseValue = (base as Record<string, unknown>)[key];
    merged[key] =
      isPlainObject(baseValue) && isPlainObject(overrideValue)
        ? deepMerge(baseValue, overrideValue)
        : overrideValue === undefined
          ? baseValue
          : overrideValue;
  }
  return merged as T;
};

// Create i18n provider with default language
const defaultLocale = getDefaultLanguage();
export const i18nProvider = polyglotI18nProvider(
  (locale: string) => {
    const base = en as TranslationMessages;
    if (locale === 'en') {
      return base;
    }
    const messages = (translations[locale as AvailableLanguage] || en) as TranslationMessages;
    return deepMerge(base, messages);
  },
  defaultLocale,
  [
    { locale: 'en', name: 'English' },
    { locale: 'zh-TW', name: '繁體中文' },
  ]
);

// Save language preference to localStorage and backend
export const saveLanguagePreference = async (language: AvailableLanguage, userId?: string) => {
  try {
    // Save to localStorage if available
    if (typeof localStorage !== 'undefined' && localStorage) {
      localStorage.setItem('language', language);
      localStorage.setItem('RaStore.locale', language);
    }
  } catch (error) {
    console.warn('Failed to save language to localStorage:', error);
  }
  
  // Save to backend if user is logged in
  if (userId) {
    try {
      const { API_CONFIG } = await import('../config/api');
      const { apiClient } = await import('../utils/apiClient');
      
      await apiClient.put(`${API_CONFIG.endpoints.backend.users}/${userId}/preferences`, {
        language,
      });
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  }
};